'use client'

import { useRef, useState } from 'react'
import GfSectionMark from '@/components/common/GfSectionMark'
import GfCompareLedger from '@/components/country/GfCompareLedger'
import { ICONS } from '@/components/common/gfIcons'
import { t } from '@/i18n/translations'

/**
 * "Gallery · Field" region explorer for the cities page. Combines two sections
 * that share one piece of state (`region`):
 *
 *   #scenarios — one card per region (name + 2-sentence description + city count)
 *   #compare   — the cities ledger, filtered by the selected region
 *
 * Clicking a region card selects that region in the ledger AND smooth-scrolls
 * down to it — so the chips below reflect the pick (the previous version only
 * scrolled, leaving the ledger on "All regions").
 *
 * Props:
 *  - scenarios : [{ key, label, count, countLabel, iconKey, description }]
 *  - rows      : compare-ledger rows [{ id, name, href, region, regionValue, season, seasonSort }]
 *  - regions   : [{ value, label, count }] (only regions that have cities)
 */
export default function GfRegionExplorer({ scenarios = [], rows = [], regions = [], lang = 'en', countryName = '' }) {
    const ui = t(lang, 'countryWidgetsUi')
    const [region, setRegion] = useState('all')
    const compareRef = useRef(null)

    const pickRegion = (value) => {
        setRegion(value)
        // Scroll the ledger into view; `scroll-margin-top` on the section leaves
        // room for the sticky sub-nav (robust against reflow, unlike manual math).
        compareRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    return (
        <>
            {/* ── 02 · REGION CARDS ────────────────────────────── */}
            {scenarios.length > 1 && (
                <section className="gf-sec" id="scenarios" style={{ paddingTop: 0 }}>
                    <GfSectionMark
                        eyebrow={ui.scenarios.eyebrow}
                        tag={ui.scenarios.tag}
                        title={ui.scenarios.title}
                    />
                    <p className="gf-lead">{ui.scenarios.lead}</p>
                    <div className="gf-scen cols-3">
                        {scenarios.map((s) => (
                            <button
                                type="button"
                                key={s.key}
                                className={`sc rg${region === s.key ? ' is-active' : ''}`}
                                aria-pressed={region === s.key}
                                onClick={() => pickRegion(s.key)}
                            >
                                <span className="n">{s.countLabel}</span>
                                <div className="ri-row">{ICONS[s.iconKey] || ICONS.grid}<h3>{s.label}</h3></div>
                                <p>{s.description}</p>
                                <span className="go">{ui.scenarios.go}</span>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {/* ── 03 · COMPARE (ledger) ────────────────────────── */}
            {rows.length > 0 && (
                <section className="gf-sec" id="compare" style={{ paddingTop: 0, scrollMarginTop: 80 }} ref={compareRef}>
                    <GfSectionMark
                        eyebrow={ui.compare.eyebrow}
                        title={ui.compare.title}
                    />
                    <p className="gf-lead">{ui.compare.lead}</p>
                    <GfCompareLedger
                        rows={rows}
                        regions={regions}
                        lang={lang}
                        countryName={countryName}
                        region={region}
                        onRegionChange={setRegion}
                    />
                </section>
            )}
        </>
    )
}
